import React from "react";
import { Linkedin } from "lucide-react";
import { useContent } from "../../content/ContentContext";
import { absUrl } from "../../api";

const PlaceholderPortrait = () => (
  <div
    className="relative w-full aspect-[4/5] rounded-[1.5rem] overflow-hidden shadow-[0_30px_60px_-20px_rgba(180,43,106,0.25)]"
    role="img"
    aria-label="Portrait placeholder for Rochelle Cannington. A high resolution headshot will be added soon."
    data-testid="about-portrait"
  >
    <div className="absolute inset-0 bg-blush-50" />
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(60% 55% at 35% 35%, rgba(180,43,106,0.22) 0%, rgba(180,43,106,0.08) 35%, transparent 65%), radial-gradient(40% 35% at 75% 25%, rgba(201,169,97,0.28) 0%, transparent 60%), radial-gradient(50% 45% at 70% 80%, rgba(180,43,106,0.10) 0%, transparent 65%)",
      }}
    />
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <span className="font-serif italic text-ink/85 text-[7rem] md:text-[8rem] leading-none">
        R<span className="text-magenta">.</span>C
      </span>
      <span className="mt-4 sc-eyebrow text-ink/70">Rochelle Cannington</span>
      <span className="mt-2 font-serif italic text-ink2 text-base md:text-lg">See Chelle, founder</span>
    </div>
    <div className="absolute top-5 right-5 w-10 h-10 border-t border-r border-gold/70" />
    <div className="absolute bottom-5 left-5 w-10 h-10 border-b border-l border-gold/70" />
  </div>
);

export const About = ({ linkedinUrl, headshotUrl }) => {
  const c = useContent("about");
  const showPhoto = !!headshotUrl;
  return (
    <section id="about" data-testid="about-section" className="sc-section">
      <div className="sc-container">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="relative max-w-sm mx-auto md:mx-0">
              <div
                aria-hidden="true"
                className="absolute -inset-3 -z-10 rounded-[2rem] opacity-70"
                style={{
                  background:
                    "radial-gradient(60% 60% at 30% 30%, rgba(180,43,106,0.10), transparent 70%), radial-gradient(60% 60% at 80% 80%, rgba(201,169,97,0.12), transparent 70%)",
                }}
              />
              {showPhoto ? (
                <img
                  src={absUrl(headshotUrl)}
                  alt="Rochelle Cannington, founder of See Chelle"
                  className="w-full aspect-[4/5] object-cover rounded-[1.5rem] shadow-[0_30px_60px_-20px_rgba(180,43,106,0.25)]"
                  data-testid="about-portrait"
                  loading="lazy"
                />
              ) : (
                <PlaceholderPortrait />
              )}
              <div className="absolute -bottom-4 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-70" />
            </div>
          </div>

          <div className="md:col-span-7">
            <span className="sc-eyebrow">{c.eyebrow}</span>
            <h2 className="sc-h2 mt-3" data-testid="about-heading">
              {c.heading_pre}{" "}
              <em className="not-italic">{c.heading_em}</em>
            </h2>

            <div className="mt-6 space-y-5">
              {(c.paragraphs || []).map((p, i) => (
                <p key={i} className={i === 0 ? "sc-lede" : "sc-body"}>{p}</p>
              ))}
            </div>

            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 text-sm font-body sc-link"
              data-testid="about-linkedin-link"
            >
              <Linkedin size={16} strokeWidth={1.8} />
              {c.linkedin_label}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
